import React from 'react'
import {
  type AppBskyActorDefs,
  moderateProfile,
  type ModerationOpts,
} from '@atproto/api'
import {keepPreviousData, useQuery, useQueryClient} from '@tanstack/react-query'

import {fetchVerifiedUser} from '#/lib/api/verified'
import {isJustAMute, moduiContainsHideableOffense} from '#/lib/moderation'
import {logger} from '#/logger'
import {STALE} from '#/state/queries'
import {useAgent} from '#/state/session'
import {useModerationOpts} from '../preferences/moderation-opts'
import {DEFAULT_LOGGED_OUT_PREFERENCES} from './preferences'

const DEFAULT_MOD_OPTS = {
  userDid: undefined,
  prefs: DEFAULT_LOGGED_OUT_PREFERENCES.moderationPrefs,
}

const RQKEY_ROOT = 'actor-autocomplete'
export const RQKEY = (prefix: string) => [RQKEY_ROOT, prefix]

export function useActorAutocompleteQuery(
  prefix: string,
  maintainData?: boolean,
  limit?: number,
) {
  const moderationOpts = useModerationOpts()
  const agent = useAgent()

  prefix = prefix.toLowerCase().trim()
  if (prefix.endsWith('.')) {
    // Going from "foo" to "foo." should not clear matches.
    prefix = prefix.slice(0, -1)
  }

  return useQuery<AppBskyActorDefs.ProfileViewBasic[]>({
    staleTime: STALE.MINUTES.ONE,
    queryKey: RQKEY(prefix || ''),
    async queryFn() {
      const [typeaheadRes, verifiedRes] = await Promise.all([
        prefix
          ? agent.searchActorsTypeahead({
              q: prefix,
              limit: limit || 8,
            })
          : undefined,
        prefix ? fetchVerifiedUser({handle: prefix}) : null,
      ])
      const actors = typeaheadRes?.data.actors ?? []
      const verified = verifiedRes
      if (verified?.custom_handle && prefix) {
        const custom = verified.custom_handle.trim().toLowerCase()
        const matches =
          custom === prefix ||
          custom.startsWith(prefix) ||
          prefix.startsWith(custom)
        if (matches && !actors.some(a => a.did === verified.did)) {
          const synthetic: AppBskyActorDefs.ProfileViewBasic = {
            did: verified.did,
            handle: verified.custom_handle!.trim(),
            displayName: verified.display_name ?? undefined,
            avatar: undefined,
            associated: undefined,
            viewer: undefined,
            labels: undefined,
          }
          return [synthetic, ...actors]
        }
      }
      return actors
    },
    select: React.useCallback(
      (data: AppBskyActorDefs.ProfileViewBasic[]) => {
        return computeSuggestions({
          q: prefix,
          searched: data,
          moderationOpts: moderationOpts || DEFAULT_MOD_OPTS,
        })
      },
      [prefix, moderationOpts],
    ),
    placeholderData: maintainData ? keepPreviousData : undefined,
  })
}

export type ActorAutocompleteFn = ReturnType<typeof useActorAutocompleteFn>
export function useActorAutocompleteFn() {
  const queryClient = useQueryClient()
  const moderationOpts = useModerationOpts()
  const agent = useAgent()

  return React.useCallback(
    async ({query, limit = 8}: {query: string; limit?: number}) => {
      query = query.toLowerCase().trim()
      let searched: AppBskyActorDefs.ProfileViewBasic[] | undefined
      let verified: Awaited<ReturnType<typeof fetchVerifiedUser>> = null
      if (query) {
        try {
          const [typeaheadRes, verifiedRes] = await Promise.all([
            queryClient.fetchQuery({
              staleTime: STALE.MINUTES.ONE,
              queryKey: RQKEY(query || ''),
              queryFn: () =>
                agent.searchActorsTypeahead({
                  q: query,
                  limit,
                }),
            }),
            fetchVerifiedUser({handle: query}),
          ])
          searched = typeaheadRes?.data.actors
          verified = verifiedRes
        } catch (e) {
          logger.error('useActorSearch: searchActorsTypeahead failed', {
            message: e,
          })
        }
      }

      let list = computeSuggestions({
        q: query,
        searched,
        moderationOpts: moderationOpts || DEFAULT_MOD_OPTS,
      })

      if (verified?.custom_handle && query) {
        const custom = verified.custom_handle.trim().toLowerCase()
        const matches =
          custom === query || custom.startsWith(query) || query.startsWith(custom)
        if (matches && !list.some(p => p.did === verified!.did)) {
          const synthetic: AppBskyActorDefs.ProfileViewBasic = {
            did: verified.did,
            handle: verified.custom_handle!.trim(),
            displayName: verified.display_name ?? undefined,
            avatar: undefined,
            associated: undefined,
            viewer: undefined,
            labels: undefined,
          }
          list = [synthetic, ...list]
        }
      }

      return list
    },
    [queryClient, moderationOpts, agent],
  )
}

function computeSuggestions({
  q,
  searched = [],
  moderationOpts,
}: {
  q?: string
  searched?: AppBskyActorDefs.ProfileViewBasic[]
  moderationOpts: ModerationOpts
}) {
  let items: AppBskyActorDefs.ProfileViewBasic[] = []
  for (const item of searched) {
    if (!items.find(item2 => item2.handle === item.handle)) {
      items.push(item)
    }
  }
  return items.filter(profile => {
    const modui = moderateProfile(profile, moderationOpts).ui('profileList')
    const isExactMatch = q && profile.handle.toLowerCase() === q
    return (
      (isExactMatch && !moduiContainsHideableOffense(modui)) ||
      !modui.filter ||
      isJustAMute(modui)
    )
  })
}
